import { useState } from 'react';
import { useArtifactStore } from '../hooks/useArtifactStore';
import type { WidgetProps } from './WidgetRegistry';
import type { VersionedArtifact, SqlContent, PromptContent } from '../services/api';

const KIND_COLORS: Record<string, string> = {
  created: 'bg-primary',
  edited: 'bg-warning',
  tested: 'bg-purpleToken',
  promoted: 'bg-success',
  demoted: 'bg-error',
  rolled_back: 'bg-error',
};

const VALIDATION_COLORS: Record<string, string> = {
  passed: 'ring-success',
  failed: 'ring-error',
  pending: 'ring-tower-muted',
  skipped: 'ring-tower-muted',
};

function getContent(a: VersionedArtifact): string {
  if (a.content.type === 'sql') return (a.content as SqlContent).sqlTemplate;
  if (a.content.type === 'prompt') return (a.content as PromptContent).systemPrompt;
  return JSON.stringify(a.content, null, 2);
}

function computeDiff(oldText: string, newText: string): Array<{ type: 'same' | 'add' | 'remove'; text: string }> {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  const result: Array<{ type: 'same' | 'add' | 'remove'; text: string }> = [];
  const maxLen = Math.max(oldLines.length, newLines.length);
  for (let i = 0; i < maxLen; i++) {
    const o = oldLines[i];
    const n = newLines[i];
    if (o === undefined) result.push({ type: 'add', text: n });
    else if (n === undefined) result.push({ type: 'remove', text: o });
    else if (o === n) result.push({ type: 'same', text: o });
    else { result.push({ type: 'remove', text: o }); result.push({ type: 'add', text: n }); }
  }
  return result;
}

export function ArtifactTimelineWidget(_props: WidgetProps) {
  const { versions, events, activeArtifact, promote, rollback } = useArtifactStore();
  const [diffPair, setDiffPair] = useState<[number, number] | null>(null);

  if (versions.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-tower-muted text-sm">
        Select an artifact to view history
      </div>
    );
  }

  const diffLines = diffPair && versions[diffPair[0]] && versions[diffPair[1]]
    ? computeDiff(getContent(versions[diffPair[0]]), getContent(versions[diffPair[1]]))
    : null;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Version timeline */}
      <div className="px-2 py-2 border-b border-tower-border/50 shrink-0">
        <div className="text-xs text-tower-muted font-semibold mb-2">Versions</div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {versions.map((v, i) => {
            const valColor = VALIDATION_COLORS[v.validation.status] || 'ring-tower-muted';
            const isActive = v.id === activeArtifact?.id;
            const isDiffSelected = diffPair && (diffPair[0] === i || diffPair[1] === i);
            return (
              <div key={v.id} className="flex flex-col items-center gap-1 shrink-0">
                <div
                  onClick={() => {
                    if (!diffPair) setDiffPair([i, Math.min(i + 1, versions.length - 1)]);
                    else if (diffPair[0] === i) setDiffPair(null);
                    else setDiffPair([i, diffPair[0]]);
                  }}
                  className={`w-6 h-6 rounded-full ring-2 ${valColor} flex items-center justify-center text-xs cursor-pointer
                    ${v.isLive ? 'bg-success text-textOnPrimary' : 'bg-tower-border text-tower-muted'}
                    ${isActive ? 'ring-offset-2 ring-offset-tower-bg' : ''}
                    ${isDiffSelected ? 'ring-warning ring-offset-1' : ''}`}
                  title={`v${v.version} — ${v.validation.status}${v.isLive ? ' (LIVE)' : ''}\n${v.changeNote}\nClick to diff`}
                >
                  {v.version}
                </div>
                <span className="text-[10px] text-tower-muted">{v.isLive ? 'LIVE' : ''}</span>
                {!v.isLive && (
                  <button
                    onClick={() => promote(v.artifactId, v.id)}
                    className="text-[10px] text-tower-accent hover:underline">
                    promote
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2 mt-1">
          {versions.length >= 2 && (
            <button
              onClick={() => activeArtifact && rollback(activeArtifact.artifactId)}
              className="text-[10px] text-error hover:underline">
              Rollback to previous
            </button>
          )}
          {diffPair && (
            <button onClick={() => setDiffPair(null)} className="text-[10px] text-tower-muted hover:underline ml-auto">
              Close diff
            </button>
          )}
        </div>
      </div>

      {/* Diff view */}
      {diffLines && (
        <div className="border-b border-tower-border/50 max-h-48 overflow-y-auto bg-tower-bg/50 shrink-0">
          <div className="text-[9px] text-tower-muted px-2 py-1 font-semibold uppercase tracking-wider bg-tower-border/20">
            Diff: v{versions[diffPair![0]].version} → v{versions[diffPair![1]].version}
          </div>
          <pre className="text-[10px] font-mono px-2 py-1 leading-relaxed">
            {diffLines.map((line, i) => (
              <div
                key={i}
                className={
                  line.type === 'add' ? 'text-success bg-success/10' :
                  line.type === 'remove' ? 'text-error bg-error/10 line-through' :
                  'text-tower-muted'
                }
              >
                {line.type === 'add' ? '+ ' : line.type === 'remove' ? '- ' : '  '}{line.text}
              </div>
            ))}
          </pre>
        </div>
      )}

      {/* Event log */}
      <div className="flex-1 overflow-y-auto p-2">
        <div className="text-xs text-tower-muted font-semibold mb-1">Audit Trail</div>
        {events.length === 0 && <div className="text-tower-muted text-xs">No events</div>}
        {events.map((evt) => {
          const dotColor = KIND_COLORS[evt.kind] || 'bg-tower-muted';
          const time = new Date(evt.ts).toLocaleString();
          return (
            <div key={evt.id} className="flex gap-2 py-1 text-xs border-b border-tower-border/30">
              <div className={`w-2 h-2 rounded-full ${dotColor} mt-1 shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className="flex gap-2">
                  <span className="text-tower-muted font-mono">{time}</span>
                  <span className="text-tower-accent font-semibold">{evt.kind}</span>
                </div>
                {evt.testResult && (
                  <div className={`mt-0.5 ${evt.testResult.success ? 'text-success' : 'text-error'}`}>
                    {evt.testResult.success
                      ? `${evt.testResult.rowCount} rows in ${evt.testResult.executionMs}ms`
                      : evt.testResult.errorMessage}
                  </div>
                )}
                {evt.payload?.changeNote != null && (
                  <div className="text-tower-muted truncate">{String(evt.payload.changeNote)}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
